<template>
   <div class="card">
      <div class="card-body m-2">
         <div class="row p-2 align-items-center">

            <!-- Search + Add -->
            <div class="col-12 col-lg-4 mb-2 mb-lg-0 d-flex gap-2">
               <input v-model="search" type="text" class="form-control form-control-sm search-input"
                  placeholder="&#xF002; Search Government TLDs..."
                  @keyup="onSearch()" />
            </div>

            <div class="col-12 col-lg-auto d-flex mb-2 mb-lg-0 gap-2">
               <button type="button" class="fw-bold btn btn-sm add-btn"
                  @click="showAddModal = true">
                  <i class="fa-solid fa-plus me-1"></i> Add Government TLD
               </button>
            </div>

            <!-- Filters + Pagination -->
            <div class="col-12 col-lg d-flex flex-wrap justify-content-lg-end align-items-center">

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Sort by:</span>
                  <select v-model="sortBy" class="form-select shadow-none filterOptions bordered-select">
                     <option v-for="s in sortByList" :value="s.value" :key="s.value">{{ s.text }}</option>
                  </select>
               </div>

               <div class="d-flex align-items-center me-3 mb-2 mb-md-0">
                  <span class="p-2 filterTitle">Rows per page:</span>
                  <select v-model="pagination.pageSize"
                     class="form-select form-select-sm shadow-none filterOptions bordered-select" style="width: auto;">
                     <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}</option>
                  </select>
               </div>

               <div class="pagination" v-if="pagination.totalItems > 0">
                  <button @click="previousPage()" :disabled="pagination.page == 1" class="pagination-arrow">
                     <i class="fa fa-chevron-left"></i>
                  </button>
                  <span class="pagination-label">
                     <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
                  </span>
                  <button @click="nextPage()"
                     :disabled="pagination.page == pagination.totalPages || pagination.totalItems == 0"
                     class="pagination-arrow">
                     <i class="fa fa-chevron-right"></i>
                  </button>
               </div>

            </div>
         </div>

         <!-- Table -->
         <div class="row p-2">
            <table class="table align-middle mainTable">
               <thead class="align-middle">
                  <tr>
                     <th scope="col">TLD</th>
                     <th scope="col">Description (Country / Region)</th>
                     <th scope="col" style="width: 80px;">Actions</th>
                  </tr>
               </thead>

               <tbody v-if="pagination.totalItems <= 0">
                  <tr>
                     <td colspan="3">No records to display.</td>
                  </tr>
               </tbody>

               <tbody v-else>
                  <tr v-for="(tldItem, index) in governmentTlds" :key="index" class="table-row-hover">
                     <td class="fw-bold text-dark font-monospace">
                        <i class="fa-solid fa-building me-2 text-cyan"></i>
                        {{ tldItem.tld }}
                     </td>
                     <td class="text-muted">{{ tldItem.description || '—' }}</td>
                     <td>
                        <button type="button" class="ms-action-btn ms-action-btn--delete"
                           @click="confirmDelete(tldItem)">
                           <i class="fa-solid fa-xmark"></i>
                        </button>
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>

      </div>
   </div>

   <!-- Add Modal -->
   <div class="modal-backdrop-custom" v-if="showAddModal" @click.self="closeAddModal()">
      <div class="modal-content-custom">
         <button @click="closeAddModal()" class="close-button-custom">&times;</button>
         <h5 class="modal-title-custom">
            <i class="fa-solid fa-building me-2 text-cyan"></i>
            Add Government TLD
         </h5>
         <hr>
         <div class="modal-body-custom">
            <div class="mb-3">
               <label class="filter-label">TLD</label>
               <input type="text" class="form-control form-control-sm"
                  style="border-radius: 8px;"
                  placeholder="e.g. gov.uk or .gov.uk"
                  v-model="inputTld" v-on:keyup.enter="addGovernmentTld()">
               <small class="text-muted mt-1 d-block">Top-level domain extension (e.g. gov.ph, .gov.au).</small>
            </div>

            <div class="mb-3">
               <label class="filter-label">Description</label>
               <input type="text" class="form-control form-control-sm"
                  style="border-radius: 8px;"
                  placeholder="e.g. United Kingdom"
                  v-model="inputDescription">
               <small class="text-muted mt-1 d-block">The country/region or organisation associated with this TLD.</small>
            </div>
         </div>
         <div class="modal-footer-custom">
            <button type="button" class="btn btn-sm btn-modal-cancel" @click="closeAddModal()">Cancel</button>
            <button type="button" class="btn btn-sm text-light save-btn"
               @click="addGovernmentTld()">
               <i class="fa-solid fa-plus me-1"></i> Add
            </button>
         </div>
      </div>
   </div>

   <!-- Delete Confirm Modal -->
   <div class="modal-backdrop-custom" v-if="showDeleteModal" @click.self="showDeleteModal = false">
      <div class="modal-content-custom">
         <button @click="showDeleteModal = false" class="close-button-custom">&times;</button>
         <h5 class="modal-title-custom">
            <i class="fa-solid fa-xmark me-2 text-danger"></i>
            Delete Government TLD
         </h5>
         <hr>
         <div class="modal-body-custom">
            <p>Are you sure you want to remove this TLD from the government master list?</p>
            <div class="site-display">{{ deleteTldName }}</div>
         </div>
         <div class="modal-footer-custom">
            <button type="button" class="btn btn-sm btn-modal-cancel" @click="showDeleteModal = false">Cancel</button>
            <button type="button" class="btn btn-sm text-light btn-danger" style="border-radius: 8px;"
               @click="deleteGovernmentTld()">
               <i class="fa-solid fa-xmark me-1"></i> Delete
            </button>
         </div>
      </div>
   </div>
</template>

<script>
import { debounce, extractErrorMessage } from '@/plugins/helper.js'

export default {
   data() {
      return {
         showAddModal: false,
         showDeleteModal: false,
         deleteID: 0,
         deleteTldName: '',
         search: "",
         governmentTlds: [],
         inputTld: "",
         inputDescription: "",
         debounceFn: null,
         paginationData: {},
         pagination: {
            pageSize: 50, page: 1, totalItems: 0, totalPages: 1,
         },
         rowsPerPageList: [
            { text: "50", value: 50 },
            { text: "75", value: 75 },
            { text: "100", value: 100 },
         ],
         sortByList: [
            { text: "ID: Newest", value: "id__desc" },
            { text: "ID: Oldest", value: "id__asc" },
            { text: "TLD: A-Z", value: "tld__asc" },
            { text: "TLD: Z-A", value: "tld__desc" },
         ],
         sortBy: "id__desc",
         sorting: { field: "id", order: "desc" },
      }
   },

   computed: {
      paginationLabel() {
         var start = Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems)
         var end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems)
         return start + " - " + end
      },
      currentPage() { return this.pagination.page },
      rowsPerPage() { return this.pagination.pageSize },
      sortItems() { return this.sortBy },
   },

   watch: {
      currentPage() { this.getGovernmentTlds() },
      rowsPerPage() { this.pagination.page = 1; this.getGovernmentTlds() },
      sortItems() {
         if (this.sortBy.includes("__")) {
            let sort = this.sortBy.split("__")
            this.sorting.field = sort[0]
            this.sorting.order = sort[1]
         } else {
            this.sorting.field = this.sortBy
            this.sorting.order = ""
         }
         this.getGovernmentTlds()
      },
   },

   created() {
      this.debounceFn = debounce(() => this.getGovernmentTlds(), 500)
      this.getGovernmentTlds()
   },

   methods: {
      isValidTld(tld) {
         // Should match simple formats like .gov.uk, gov.uk, .gov etc.
         const tldPattern = /^\.?[a-zA-Z0-9.-]+\.[a-zA-Z0-9.-]+$/;
         const simplePattern = /^\.?[a-zA-Z]{2,}$/;
         return tldPattern.test(tld) || simplePattern.test(tld);
      },

      confirmDelete(tldItem) {
         this.deleteID = tldItem.id
         this.deleteTldName = tldItem.tld
         this.showDeleteModal = true
      },

      closeAddModal() {
         this.showAddModal = false
         this.inputTld = ""
         this.inputDescription = ""
      },

      previousPage() { this.pagination.page > 1 ? this.pagination.page -= 1 : '' },
      nextPage() { this.pagination.totalPages > this.pagination.page ? this.pagination.page += 1 : '' },
      onSearch() { this.pagination.page = 1; this.debounceFn() },

      async addGovernmentTld() {
         try {
            if (!this.inputTld || !this.isValidTld(this.inputTld)) {
               this.$toast.error("Please provide a valid TLD (e.g. .gov, gov.uk).")
               return
            }
            // Ensure format doesn't have multiple leading dots, and lowercased
            let tldValue = this.inputTld.trim().toLowerCase();
            // Let's store TLDs starting with a dot if not present, to match common convention in DB logic
            if (!tldValue.startsWith('.')) {
               tldValue = '.' + tldValue;
            }

            const payload = {
               tld: tldValue,
               description: this.inputDescription.trim()
            }
            const resp = await this.$axios.post("admin-controls/create-government-tld", payload)
            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message)
               this.getGovernmentTlds()
            }
            this.closeAddModal()
         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to add government TLD."))
            this.closeAddModal()
         }
      },

      async deleteGovernmentTld() {
         try {
            const resp = await this.$axios.delete("admin-controls/remove-government-tld/" + this.deleteID)
            if (resp.data.success != null && resp.data.success == false) {
               this.$toast.error(resp.data.message)
            } else {
               this.$toast.success(resp.data.message)
               this.getGovernmentTlds()
            }
            this.deleteID = 0
            this.deleteTldName = ''
            this.showDeleteModal = false
         } catch (error) {
            console.log(error)
            this.$toast.error(extractErrorMessage(error, "Failed to delete government TLD."))
            this.deleteID = 0
            this.showDeleteModal = false
         }
      },

      async getGovernmentTlds() {
         let apiPath = "admin-controls/get-government-tlds?page_size=" + this.pagination.pageSize + "&page=" + this.pagination.page

         if (this.sorting.order != "" && this.sorting.field != "") {
            apiPath += "&order_by=" + this.sorting.order
            apiPath += "&field=" + this.sorting.field
         }
         if (this.search !== "" && this.search !== null) {
            apiPath += "&search=" + encodeURIComponent(this.search)
         }

         await this.$axios.get(apiPath)
            .then((response) => {
               if (response.data.government_tlds === "No Record Found")
                  this.governmentTlds = []
               else {
                  this.governmentTlds = response.data.government_tlds
               }
               this.paginationData = response.data.pagination
               this.pagination.totalItems = this.paginationData.total_count
               this.pagination.totalPages = Math.ceil(this.paginationData.total_count / this.pagination.pageSize)
            })
            .catch((error) => {
               console.log(error)
               this.$toast.error(extractErrorMessage(error, "Failed to load government TLDs."))
            })
      },
   },
}
</script>

<style scoped>
.card { border: none; }

.search-input {
   border-radius: 8px;
   height: 40px;
   font-size: 14px;
   font-family: 'Roboto', sans-serif, 'Font Awesome 6 Free';
   font-weight: 900;
}

.add-btn {
   font-size: 14px;
   height: 40px;
   border-radius: 8px !important;
   padding: 0 1.5rem;
   white-space: nowrap;
   background: #00A0C1;
   color: #fff;
}
.add-btn:hover {
   background: #008ba8;
   color: #fff;
}

.save-btn {
   background: #00A0C1;
   border-radius: 8px;
}
.save-btn:hover {
   background: #008ba8;
}

.text-cyan {
   color: #00A0C1;
}

.table-row-hover:hover {
   background-color: #fcfdfe;
}

.site-display {
   font-family: monospace; font-size: 13px;
   background: #e6f7fa; padding: 8px 12px;
   border-radius: 8px; color: #008ba8;
   word-break: break-all;
}

/* Modal */
.modal-backdrop-custom {
   position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
   background-color: rgba(0,0,0,0.5); display: flex;
   justify-content: center; align-items: center; z-index: 1050;
}
.modal-content-custom {
   position: relative; background: #fff; padding: 2rem;
   border-radius: 14px; width: 100%; max-width: 480px;
   box-shadow: 0 8px 30px rgba(0,0,0,0.2);
}
.modal-title-custom { font-weight: 700; font-size: 1.1rem; color: #435666; margin-bottom: 0; }
.modal-content-custom hr { margin: 0.75rem 0 1rem; border-color: #e9ecef; }
.close-button-custom {
   position: absolute; top: 0.75rem; right: 1rem;
   font-size: 1.8rem; font-weight: bold; line-height: 1;
   color: #adb5bd; background: none; border: none; cursor: pointer;
}
.close-button-custom:hover { color: #435666; }
.modal-body-custom { font-size: 14px; color: #435666; }
.filter-label {
   font-size: 11px; font-weight: 600; color: #6c757d;
   text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 4px;
   display: block;
}
.modal-footer-custom { margin-top: 1rem; display: flex; justify-content: flex-end; gap: 8px; }
.btn-modal-cancel { background: #e9ecef; border: none; border-radius: 8px; color: #435666; }
</style>
